import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const modalContent: Record<'privacy' | 'terms', {
  title: string;
  sections: {
    heading: string;
    body: string;
  }[];
}> = {
  privacy: {
    title: 'Privacy Policy',
    sections: [{
      heading: 'What We Collect',
      body: 'When you purchase Grandma & Me: Memory Tickets, we collect only the information necessary to process your order — your email address and payment details. We do not collect data from children or grandchildren.'
    }, {
      heading: 'How We Use Your Information',
      body: 'Your email is used solely to deliver your digital download and send your receipt. We will never sell, rent, or share your personal information with third parties.'
    }, {
      heading: 'Cookies & Tracking',
      body: 'Our website uses minimal cookies required for the shopping experience. We do not use invasive tracking or retargeting technologies.'
    }, {
      heading: 'Your Rights',
      body: 'You may request deletion of your data at any time. Simply reach out and we will remove your information from our records promptly.'
    }, {
      heading: 'Updates',
      body: 'We may update this policy from time to time. Any changes will be reflected on this page with the updated date.'
    }]
  },
  terms: {
    title: 'Terms of Service',
    sections: [{
      heading: 'Digital Product License',
      body: 'When you purchase Grandma & Me: Memory Tickets, you receive a personal-use license to download, print, and use the materials for your own family. You may not resell, redistribute, or commercially reproduce the content.'
    }, {
      heading: 'Refund Policy',
      body: "If the product doesn't feel like something you'd be proud to gift, we'll refund your purchase — no questions asked. Simply contact us within 30 days of purchase."
    }, {
      heading: 'Permitted Use',
      body: 'You may print multiple copies for personal gifting within your family. Each purchase is intended for one household. Please do not share the digital files publicly or post them online.'
    }, {
      heading: 'Disclaimer',
      body: 'Grandma & Me: Memory Tickets is a printable activity product. We make no guarantees about specific outcomes, but we believe in the power of quality time together.'
    }, {
      heading: 'Changes to Terms',
      body: 'We reserve the right to update these terms. Continued use of the product after changes constitutes acceptance of the new terms.'
    }]
  }
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeModal]);
  return <>
      <footer className="py-10 text-center border-t border-warm-gray/10" data-id="element-63">
        <div className="flex items-center justify-center gap-4 mb-4" data-id="element-64">
          <button onClick={() => setActiveModal('privacy')} className="text-sm text-warm-gray/70 hover:text-coral transition-colors underline underline-offset-2 decoration-warm-gray/30 hover:decoration-coral" data-id="element-65">
            Privacy Policy
          </button>
          <span className="text-warm-gray/30" data-id="element-66">·</span>
          <button onClick={() => setActiveModal('terms')} className="text-sm text-warm-gray/70 hover:text-coral transition-colors underline underline-offset-2 decoration-warm-gray/30 hover:decoration-coral" data-id="element-67">
            Terms of Service
          </button>
        </div>
        <p className="text-warm-gray/50 text-xs" data-id="element-68">
          © {new Date().getFullYear()} Grandma & Me. All rights reserved.
        </p>
      </footer>

      <AnimatePresence data-id="element-69">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-70">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="bg-cream rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden border border-warm-gray/10" data-id="element-71">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-warm-gray/10" data-id="element-72">
                <h2 className="font-serif text-2xl font-bold text-charcoal" data-id="element-73">
                  {modalContent[activeModal].title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full text-warm-gray hover:bg-warm-gray/10 hover:text-charcoal transition-colors" aria-label="Close" data-id="element-74">
                  <X className="w-5 h-5" data-id="element-75" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] space-y-6" data-id="element-76">
                {modalContent[activeModal].sections.map((section, i) => <div key={i} data-id="element-77">
                    <h3 className="font-semibold text-charcoal mb-2 text-sm uppercase tracking-wide" data-id="element-78">
                      {section.heading}
                    </h3>
                    <p className="text-warm-gray leading-relaxed text-[15px]" data-id="element-79">
                      {section.body}
                    </p>
                  </div>)}
                <p className="text-xs text-warm-gray/50 pt-4 border-t border-warm-gray/10" data-id="element-80">
                  Last updated: February 2026
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}